import { FC, JSX, useCallback, useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';

import {
  clearAlertMessage,
  getAlertMessage,
  getSessionCurrencyTypeId,
  getSideNavPreference,
  setSideNavPreference,
} from 'app/api/cookie';
import VerticalExpander from 'app/components/animations/VerticalExpander';
import DisabledContainer from 'app/components/complex/DisabledContainer';
import Box from 'app/components/v2/components/Box';
import { IconName } from 'app/components/v2/components/Icon/types';
import { useScrollableWindow } from 'app/hooks';
import { useIsNoaIbex } from 'app/state/AuthContext/hooks';
import { useGeneralDispatch, useGeneralState } from 'app/state/GeneralContext/hooks';

import DesktopNav from './DesktopNav';
import Main from './Main';
import MobileNav from './MobileNav';
import NavContainer from './NavContainer';
import NavigationLink from './NavigationLink';
import NavWrapper from './NavWrapper';
import SessionCurrencyBanner from './SessionCurrencyBanner';
import SideNav from './SideNav';
import {
  StyledContainer,
  StyledDiv,
} from './styledComponents';
import SystemAlert from './SystemAlert';
import Wrapper from './Wrapper';

// Local Typings
export interface Link {
  activePaths: string[]; // used to style navlink
  hasNotifications?: boolean;
  iconName: IconName;
  id: string;
  label: string;
  onClick?: (evt: React.KeyboardEvent<unknown> | React.MouseEvent<unknown>) => void;
  to?: string;
}
export interface LayoutProps {
  body: JSX.Element;
}
export interface NavProps {
  logoPath: string;
}
interface Props extends LayoutProps, NavProps {
  navLinks: Link[];
}

// Local Variables
const transitionSpeed = 500;

// Component Definition
const AppLayout: FC<Props> = ({
  body,
  navLinks,
  ...props
}) => {
  const navRef = useRef<HTMLDivElement>(null);
  const sessionBannerRef = useRef<HTMLDivElement>(null);

  const alertMessage = getAlertMessage();
  const [isAlertActive, setIsAlertActive] = useState(!!alertMessage);
  const [navHeight, setNavHeight] = useState(0);

  const generalDispatch = useGeneralDispatch();
  const {
    beamerMessageCount,
    currentDevice,
    isSideNavOpen,
  } = useGeneralState();

  const isDesktopWidth = currentDevice === 'desktop';
  const isMobileWidth = currentDevice === 'mobile';
  const sessionCurrencyTypeId = getSessionCurrencyTypeId();

  const {
    scrollDirection,
    scrollPosition,
  } = useScrollableWindow();

  const isSessionBannerVisible = scrollPosition < 100 || scrollDirection === 'up';

  const handleCloseBanner = useCallback(() => {
    clearAlertMessage();
    setIsAlertActive(false);
  },
  []);

  const hasSomeNotifications = navLinks.some(link => link.hasNotifications)
    || beamerMessageCount > 0;

  useEffect(() => {
    if (navRef?.current) {
      const newHeight = navRef.current.clientHeight;
      if (newHeight !== navHeight) {
        setNavHeight(navRef.current.clientHeight);
      }
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [
    isAlertActive,
    isDesktopWidth,
    isMobile,
    isMobileWidth,
    window.location.pathname,
    sessionBannerRef,
  ]);

  useEffect(() => {
    if (!isDesktopWidth && isSideNavOpen) {
      generalDispatch({
        type: 'toggleSideNav',
      });
    } else if (isDesktopWidth) {
      const sideNavPreference = getSideNavPreference();
      const sideNavStateDiffersFromPreference =
          (sideNavPreference === 'open' && !isSideNavOpen) ||
          (sideNavPreference === 'closed' && isSideNavOpen);

      if (sideNavStateDiffersFromPreference) {
        generalDispatch({
          type: 'toggleSideNav',
        });
      }
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [isDesktopWidth]);

  useEffect(() => {
    if (isDesktopWidth) {
      setSideNavPreference(isSideNavOpen ? 'open' : 'closed');
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [isSideNavOpen]);

  const isNoaProcessorOnly = useIsNoaIbex();

  return (
    <Box
      alignItems="stretch"
      display="flex"
      flexDirection="column"
      fullHeight
    >
      <Wrapper
        navHeight={navHeight}
        ref={navRef}
      >
        <SystemAlert
          alertMessage={alertMessage}
          isActive={isAlertActive}
          onClose={handleCloseBanner}
        />

        {sessionCurrencyTypeId && (
          <VerticalExpander
            isExpanded={isSessionBannerVisible}
            transitionSpeedInMs={transitionSpeed}
          >
            <StyledDiv
              isVisible={isSessionBannerVisible}
              ref={sessionBannerRef}
            >
              <SessionCurrencyBanner />
            </StyledDiv>
          </VerticalExpander>
        )}

        <NavWrapper>
          <NavContainer>
            {isMobile || isMobileWidth
              ? (
                <MobileNav
                  hasSomeNotifications={hasSomeNotifications}
                  {...props}
                />
              )
              : <DesktopNav {...props} />}
          </NavContainer>
        </NavWrapper>
      </Wrapper>

      <StyledContainer
        alignItems="flex-start"
        display="flex"
        fullHeight
        fullWidth
        navHeight={navHeight}
      >
        {!isNoaProcessorOnly
          ? (
            <SideNav
              {...props}
              topNavHeight={navHeight}
            >
              {navLinks.map(link => (
                <NavigationLink
                  isSideNavOpen={isSideNavOpen}
                  key={link.label}
                  {...link}
                />
              ))}
            </SideNav>
          )
          : null}

        <Main>
          <DisabledContainer
            disabled={!isDesktopWidth && isSideNavOpen}
            fullHeight
            variant="scrim"
          >
            {body}
          </DisabledContainer>
        </Main>
      </StyledContainer>
    </Box>
  );
};

export default AppLayout;
